import { useMemo } from "react";
import type { EventOccurrence, EventType, Person } from "@shared/types";
import { formatDateTimeRange } from "@/utils/date";
import { occurrenceColor, participantBadges } from "@/utils/eventColor";
import { ParticipantBadges } from "./ParticipantBadges";
import { EmptyState } from "./EmptyState";

interface SearchResultsListProps {
  query: string;
  results: EventOccurrence[];
  loading: boolean;
  people: Person[];
  eventTypes: EventType[];
  onSelectOccurrence: (occurrence: EventOccurrence) => void;
}

export function SearchResultsList({ query, results, loading, people, eventTypes, onSelectOccurrence }: SearchResultsListProps) {
  const peopleById = useMemo(() => new Map(people.map((p) => [p.id, p])), [people]);
  const eventTypesById = useMemo(() => new Map(eventTypes.map((t) => [t.id, t])), [eventTypes]);

  return (
    <div className="rounded-xl border border-line dark:border-line-dark bg-surface dark:bg-surface-dark shadow-card">
      <div className="border-b border-line dark:border-line-dark px-5 py-3">
        <p className="text-sm font-medium">
          {loading ? "Searching…" : `${results.length} result${results.length === 1 ? "" : "s"} for "${query}"`}
        </p>
      </div>
      {!loading && results.length === 0 ? (
        <EmptyState
          title="No matching events"
          description="Try a different title, team, opponent, or location."
        />
      ) : (
        <ul className="divide-y divide-line dark:divide-line-dark">
          {results.map((occ) => (
            <li key={occ.occurrenceId}>
              <button
                onClick={() => onSelectOccurrence(occ)}
                className="flex w-full items-center gap-3 px-5 py-3 text-left hover:bg-canvas dark:hover:bg-canvas-dark"
              >
                <span
                  className="h-2.5 w-2.5 shrink-0 rounded-full"
                  style={{ backgroundColor: occurrenceColor(occ, eventTypesById) }}
                />
                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm font-medium">{occ.title}</p>
                  <p className="truncate text-xs text-muted dark:text-muted-dark">
                    {formatDateTimeRange(occ.occurrenceStart, occ.endAt, occ.allDay, occ.timezone)}
                    {occ.locationName ? ` · ${occ.locationName}` : ""}
                  </p>
                </div>
                <ParticipantBadges people={participantBadges(occ, peopleById)} size={16} variant="solid" />
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
